import { useTranslation } from 'react-i18next'
import { Alpha2Code, Alpha3Code, toAlpha2 } from 'i18n-iso-countries'

import { WorldFlagAvatar } from '../WorldFlagAvatar/WorldFlagAvatar'

import { FlagAvatar, Flex, Text } from '@percent/lemonade'
import { getCountryNameWithOverride } from '@percent/workplace-giving/common/hooks'
import { getShortLanguage } from '@percent/workplace-giving/utils/getShortLanguage'
import { SwitchToGlobalResultsSkeleton } from './SwitchToGlobalResultsSkeleton'
import classes from './SwitchToGlobalResults.module.scss'

type SwitchToGlobalResultsProps = Readonly<{
  totalResults: number
  currentCountryCode: string
  prevCountryCode: string
  switchToGlobal: VoidFunction
  switchToCountry: VoidFunction
  isLoading?: boolean
}>

export function SwitchToGlobalResults({
  totalResults,
  currentCountryCode,
  prevCountryCode,
  switchToGlobal,
  switchToCountry,
  isLoading = false
}: SwitchToGlobalResultsProps) {
  const { t, i18n } = useTranslation()
  const shortLanguage = getShortLanguage(i18n)

  if (isLoading) {
    return <SwitchToGlobalResultsSkeleton />
  }

  return (
    <Flex justify="flex-start" align="center" wrap="nowrap" gap="xs">
      <Text className={classes.Text} dataTestId="totalResultsCount">
        {t('workplace_giving.search.resultsIn', { count: totalResults })}
      </Text>
      {currentCountryCode ? (
        <>
          <FlagAvatar code={toAlpha2(currentCountryCode as Alpha3Code) as Alpha2Code} />
          <Text className={classes.EmphasisText}>{`${getCountryNameWithOverride(
            currentCountryCode as Alpha3Code,
            shortLanguage
          )}.`}</Text>
        </>
      ) : (
        <>
          <WorldFlagAvatar />
          <Text className={classes.EmphasisText}>{`${t('workplace_giving.search.world')}.`}</Text>
        </>
      )}
      {prevCountryCode === currentCountryCode && prevCountryCode === '' ? null : (
        <Flex align="center" gap="xs">
          <Text className={classes.Text} color="gray600">
            {t('workplace_giving.search.switchTo')}
          </Text>
          <button
            type="button"
            className={classes.SwitchToBtn}
            onClick={currentCountryCode ? switchToGlobal : switchToCountry}
            data-testid={currentCountryCode ? 'switchToGlobalBtn' : 'switchToCountryBtn'}
          >
            {currentCountryCode ? (
              <>
                <WorldFlagAvatar />
                {t('workplace_giving.search.world')}
              </>
            ) : (
              <>
                <FlagAvatar code={toAlpha2(prevCountryCode as Alpha3Code) as Alpha2Code} />
                {getCountryNameWithOverride(prevCountryCode as Alpha3Code, shortLanguage)}
              </>
            )}
          </button>
        </Flex>
      )}
    </Flex>
  )
}
